import cloneDeep from 'lodash/cloneDeep';
import type { CompleteType } from '../util/general';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import { getDefaultGroupId } from './tenantModel';
import { itemAccessControlLevels, type ItemAccessControl } from './itemAccessControlDto';

export const isInstanceOfItemAccessControlDto = (obj: unknown): obj is ItemAccessControl => {
  if (!isObject(obj)) {
    return false;
  }
  const possibleDto = obj as Partial<ItemAccessControl>;
  return (typeof possibleDto.level === 'string' && itemAccessControlLevels.includes(possibleDto.level))
    || Array.isArray(possibleDto.groups)
    || Array.isArray(possibleDto.users);
};

export const toAccessControlDto = (access: ItemAccessControl): CompleteType<ItemAccessControl> => ({
  level: access.level,
  groups: access.groups,
  users: access.users,
});

export const MAX_ACCESS_CONTROL_USERS = 50;

export type AclValidationResult =
  | { isValid: true }
  | { isValid: false, reason: string };

export const validateAccessControlUsersCap = (users: string[] | undefined): AclValidationResult => {
  if (users === undefined) {
    return { isValid: true };
  }
  if (users.length > MAX_ACCESS_CONTROL_USERS) {
    return { isValid: false, reason: `users array exceeds maximum of ${MAX_ACCESS_CONTROL_USERS} entries (got ${users.length})` };
  }
  return { isValid: true };
};

export interface GroupDefaultAccessControls {
  defaultVisibleTo?: ItemAccessControl | null;
  defaultUpdatableBy?: ItemAccessControl | null;
}

export const validateGroupDefaultAccessControl = (group: GroupDefaultAccessControls): AclValidationResult => {
  const reasons: string[] = [];
  const visibility = validateAccessControlUsersCap(group.defaultVisibleTo?.users);
  if (!visibility.isValid) {
    reasons.push(`defaultVisibleTo: ${visibility.reason}`);
  }
  const updatability = validateAccessControlUsersCap(group.defaultUpdatableBy?.users);
  if (!updatability.isValid) {
    reasons.push(`defaultUpdatableBy: ${updatability.reason}`);
  }
  if (reasons.length > 0) {
    return { isValid: false, reason: reasons.join('; ') };
  }
  return { isValid: true };
};

/**
 * Returns default visibleTo access control for items.
 *
 * Priority:
 * 1. If primaryGroupDefaultVisibleTo is provided, use it (admin-configured per-group default)
 * 2. Otherwise, fall back to existing logic:
 *    - For existing items or when primary group is the tenant default group, return tenant-wide visibility
 *    - Otherwise, return visibility restricted to user's primary group
 *
 * @param tenantId Tenant ID of user making the request
 * @param primaryGroupId Primary group ID of the user making the request
 * @param forExistingItem Whether this request is for an existing item. This is required for backwards
 * compatibility purposes since previously, the default visibility of items used to be everyone in the tenant.
 * @param primaryGroupDefaultVisibleTo Admin-configured default visibility for the user's primary group
 */
export const getDefaultVisibleTo = (
  tenantId: string,
  primaryGroupId: string,
  forExistingItem: boolean,
  primaryGroupDefaultVisibleTo: ItemAccessControl | undefined | null,
): ItemAccessControl => {
  if (primaryGroupDefaultVisibleTo) {
    return primaryGroupDefaultVisibleTo;
  }
  const defaultGroupId = getDefaultGroupId(tenantId);
  if (forExistingItem || defaultGroupId === primaryGroupId) {
    return getDefaultAccessControl();
  }
  return {
    level: 'selected_groups',
    groups: [primaryGroupId],
    users: [],
  };
};

export const getDefaultAccessControl = (): ItemAccessControl => ({
  level: 'tenant',
  groups: [],
  users: [],
});

/**
 * Returns default updatableBy access control for items.
 *
 * Priority:
 * 1. If primaryGroupDefaultUpdatableBy is provided, use it (admin-configured per-group default)
 * 2. Otherwise, return undefined to preserve existing behavior where items without updatableBy
 *    are editable by everyone in the organization group plus the owner
 *
 * @param primaryGroupDefaultUpdatableBy Optional admin-configured default updatableBy for the user's primary group
 */
export const getDefaultUpdatableBy = (
  primaryGroupDefaultUpdatableBy?: ItemAccessControl | null,
): ItemAccessControl | undefined => {
  if (primaryGroupDefaultUpdatableBy) {
    return primaryGroupDefaultUpdatableBy;
  }

  return undefined;
};

export const getSanitizedAccessControlForIngest = (access: ItemAccessControl): { sanitizedAccess: ItemAccessControl | undefined, errorMsg?: string } => {
  const usersCap = validateAccessControlUsersCap(access.users);
  if (!usersCap.isValid) {
    return { sanitizedAccess: undefined, errorMsg: usersCap.reason };
  }

  const sanitizedAccess = cloneDeep(access);

  if (access.level === 'tenant') {
    return { sanitizedAccess: toAccessControlDto(access) };
  }

  if (sanitizedAccess.level === 'owner') {
    return { sanitizedAccess: undefined, errorMsg: `Cannot set access control level to ${sanitizedAccess.level}` };
  }

  return { sanitizedAccess: toAccessControlDto(sanitizedAccess) };
};
